<template>
  <div>
    <div class="product-finder-module" v-if="!hideBanner">
      <div class="finder-banner" @click="dialogVisible = true">
        <div class="finder-content">
          <div class="text-info">
            <h3>没找到想要的商品？</h3>
            <p>提交代找需求，雪狮专业团队为您精准找货</p>
          </div>
          <div class="action-btn">
            立即申请代找 <i class="el-icon-right"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 代找工单弹窗 -->
    <el-dialog
      title="申请代找商品"
      :visible.sync="dialogVisible"
      width="500px"
      append-to-body
      :close-on-click-modal="false"
      class="finder-dialog"
    >
      <div class="finder-form-scroll">
        <el-form :model="form" :rules="rules" ref="finderForm" label-position="top">
          <el-form-item label="商品名称" prop="productName">
            <el-input v-model="form.productName" placeholder="请填写您想找的商品名称"></el-input>
          </el-form-item>
          
          <el-form-item label="商品描述" prop="productDescription">
            <el-input 
              type="textarea" 
              v-model="form.productDescription" 
              :rows="3" 
              placeholder="请详细描述商品规格、型号、品牌等信息，帮助我们更快为您找到"
            ></el-input>
          </el-form-item>
          
          <!-- 动态字段部分 -->
          <el-form-item v-for="(field, index) in dynamicFields" :key="'df-'+index" :label="field.name">
            <el-input v-model="field.value" :placeholder="'请输入' + field.name"></el-input>
          </el-form-item>
  
          <el-form-item label="商品图片 (可选)">
            <div class="finder-uploader-container">
              <el-upload
                class="finder-uploader"
                :action="uploadUrl"
                :headers="uploadHeaders"
                :show-file-list="false"
                :on-success="handleUploadSuccess"
                :on-error="handleUploadError"
                :before-upload="beforeUpload"
              >
                <div v-if="form.images" class="preview-box">
                  <img :src="form.images" class="preview-img">
                  <div class="preview-mask">
                    <i class="el-icon-delete" @click.stop="form.images = ''"></i>
                  </div>
                </div>
                <div v-else class="upload-trigger" v-loading="uploading" element-loading-spinner="el-icon-loading">
                  <div v-if="!uploading" class="upload-inner">
                    <i class="el-icon-plus"></i>
                    <span>上传参考图片</span>
                  </div>
                </div>
              </el-upload>
            </div>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="danger" @click="handleSubmit" :loading="submitting">提交工单</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { submitAgentWorkOrder, getAgentWorkOrderFormConfig } from '@/api/agentWorkOrder'
import { getToken } from '@/utils/auth'

export default {
  name: 'ProductFinder',
  props: {
    hideBanner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      submitting: false,
      uploading: false,
      uploadUrl: process.env.VUE_APP_BASE_API + '/common/upload',
      form: {
        productName: '',
        productDescription: '',
        images: ''
      },
      rules: {
        productName: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        productDescription: [{ required: true, message: '请输入商品描述', trigger: 'blur' }]
      },
      dynamicFields: []
    }
  },
  computed: {
    uploadHeaders() {
      return {
        Authorization: 'Bearer ' + getToken()
      }
    }
  },
  watch: {
    dialogVisible(val) {
      if (val) {
        this.fetchFormConfig()
      }
    }
  },
  methods: {
    openDialog() {
      this.dialogVisible = true
    },
    async fetchFormConfig() {
      try {
        const res = await getAgentWorkOrderFormConfig()
        if (res.code === 200 && res.msg) {
          const fieldNames = JSON.parse(res.msg || '[]')
          this.dynamicFields = fieldNames.map(name => ({
            name: name,
            value: ''
          }))
        }
      } catch (error) {
        console.error('获取代找工单配置失败:', error)
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传图片大小不能超过 10MB!')
        return false
      }
      this.uploading = true
      return true
    },
    handleUploadSuccess(res) {
      this.uploading = false
      if (res.code === 200) {
        this.form.images = res.url
        this.$message.success('图片上传成功')
      } else {
        this.$message.error(res.msg || '上传失败')
      }
    },
    handleUploadError() {
      this.uploading = false
      this.$message.error('上传失败，请重试')
    },
    handleSubmit() {
      this.$refs.finderForm.validate(async (valid) => {
        if (valid) {
          if (!getToken()) {
            this.$message.warning('请先登录后再提交代找申请')
            this.$router.push('/login')
            return
          }
          this.submitting = true
          try {
            // 处理 extraInfo
            const extraInfo = JSON.stringify(this.dynamicFields.map(field => ({
              name: field.name,
              value: field.value
            })))
            
            const submitData = {
              ...this.form,
              extraInfo: extraInfo
            }
            
            const res = await submitAgentWorkOrder(submitData)
            if (res.code === 200) {
              this.$message.success('申请提交成功，我们将尽快与您联系！')
              this.dialogVisible = false
              this.resetForm()
            }
          } catch (error) {
            console.error('提交代找工单失败:', error)
          } finally {
            this.submitting = false
          }
        }
      })
    },
    resetForm() {
      this.form = {
        productName: '',
        productDescription: '',
        images: ''
      }
      this.dynamicFields = this.dynamicFields.map(f => ({ ...f, value: '' }))
      this.$nextTick(() => {
        if (this.$refs.finderForm) {
          this.$refs.finderForm.clearValidate()
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.product-finder-module {
  margin: 20px 0;
  
  .finder-banner {
    background: linear-gradient(90deg, #fdf2f2 0%, #fff 100%);
    border: 1px dashed #e1251b;
    border-radius: 8px;
    padding: 20px 30px;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(225, 37, 27, 0.1);
      transform: translateY(-2px);
    }

    .finder-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .text-info {
        h3 {
          font-size: 18px;
          color: #e1251b;
          margin-bottom: 5px;
          font-weight: bold;
        }
        p {
          font-size: 14px;
          color: #666;
        }
      }

      .action-btn {
        background-color: #e1251b;
        color: #fff;
        padding: 8px 20px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
        
        i {
          margin-left: 5px;
        }
      }
    }
  }
}

.finder-dialog {
  ::v-deep .el-dialog__body {
    padding: 0;
  }
}

.finder-form-scroll {
  max-height: 480px;
  overflow-y: auto;
  padding: 20px 25px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #f1f1f1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.finder-uploader-container {
  .finder-uploader {
    .upload-trigger {
      width: 100px;
      height: 100px;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      color: #999;
      position: relative;
      overflow: hidden;
      
      .upload-inner {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      
      i { font-size: 24px; margin-bottom: 5px; }
      span { font-size: 12px; }
      
      &:hover {
        border-color: #e1251b;
        color: #e1251b;
      }

      ::v-deep .el-loading-mask {
        background-color: rgba(255, 255, 255, 0.9);
        .el-loading-spinner {
          margin-top: -12px;
          i { color: #e1251b; font-size: 20px; }
        }
      }
    }

    .preview-box {
      width: 100px;
      height: 100px;
      position: relative;
      border: 1px solid #eee;
      border-radius: 4px;
      overflow: hidden;
      
      .preview-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      
      .preview-mask {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0;
        transition: opacity 0.3s;
        
        i {
          color: #fff;
          font-size: 20px;
          cursor: pointer;
        }
      }
      
      &:hover .preview-mask {
        opacity: 1;
      }
    }
  }
}

.dialog-footer {
  .el-button--danger {
    background-color: #e1251b;
    border-color: #e1251b;
  }
}

// 响应式适配 - 平板到PC端
@media (max-width: 1199px) and (min-width: 768px) {
  .product-finder-module .finder-banner {
    padding: 15px 20px;
    .finder-content {
      .text-info {
        h3 { font-size: 16px; }
        p { font-size: 13px; }
      }
      .action-btn {
        padding: 6px 15px;
        font-size: 13px;
      }
    }
  }
}
</style>
